import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaDownload,
  FaExpand,
} from 'react-icons/fa';

/**
 * ImageGallery Component
 * 
 * Displays images as thumbnails with a detail lightbox view.
 * Supports keyboard navigation and download functionality.
 * 
 * @component
 * @example
 * const images = [
 *   { id: 1, url: 'image1.jpg', title: 'Photo 1' },
 *   { id: 2, url: 'image2.jpg', title: 'Photo 2' },
 * ];
 * 
 * <ImageGallery 
 *   images={images} 
 *   columns={3}
 *   showDownload={true}
 * />
 */
const ImageGallery = ({
  images = [],
  columns = 3,
  gap = 'md',
  showDownload = false,
  showTitles = true,
  thumbnailHeight = 'h-40',
  onImageClick = null,
  className = '',
}) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const handlePrevious = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  }, [images.length]);

  const handleNext = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
  }, [images.length]);

  const handleImageClick = (index) => {
    setSelectedIndex(index);
    setIsOpen(true);
    if (onImageClick) {
      onImageClick(images[index], index);
    }
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          handleClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handlePrevious, handleNext, handleClose]);

  const handleDownload = useCallback(async () => {
    if (selectedIndex === null) return;

    const image = images[selectedIndex];
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = image.title || `image-${selectedIndex}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  }, [selectedIndex, images]);

  if (images.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8 tw-bg-slate-50 tw-rounded tw-border tw-border-dashed tw-border-slate-300">
        <p className="tw-text-slate-500">No images to display</p>
      </div>
    );
  }

  const gapClasses = {
    sm: 'tw-gap-2',
    md: 'tw-gap-4',
    lg: 'tw-gap-6',
  };

  const colClasses = {
    1: 'tw-grid-cols-1',
    2: 'tw-grid-cols-2',
    3: 'tw-grid-cols-3',
    4: 'tw-grid-cols-4',
    5: 'tw-grid-cols-5',
    6: 'tw-grid-cols-6',
  };

  const currentImage = selectedIndex !== null ? images[selectedIndex] : null;

  return (
    <>
      {/* Thumbnail Grid */}
      <div className={`tw-mb-4 ${className}`}>
        <div
          className={`tw-grid ${colClasses[columns]} ${gapClasses[gap]}`}
        >
          {images.map((image, index) => (
            <div
              key={image.id || index}
              className="tw-group tw-cursor-pointer tw-relative tw-overflow-hidden tw-rounded tw-bg-slate-100 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow"
              onClick={() => handleImageClick(index)}
              role="button"
              tabIndex={0}
              aria-label={`View ${image.title || `image ${index + 1}`}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleImageClick(index);
                }
              }}
            >
              {/* Thumbnail Image */}
              <img
                src={image.url}
                alt={image.title || `Gallery image ${index + 1}`}
                className={`${thumbnailHeight} tw-w-full tw-object-cover tw-group-hover:tw-scale-110 tw-transition-transform tw-duration-200`}
              />

              {/* Overlay with Icon */}
              <div className="tw-absolute tw-inset-0 tw-bg-black tw-opacity-0 hover:tw-opacity-30 tw-transition-opacity tw-flex tw-items-center tw-justify-center">
                <FaExpand className="tw-text-white tw-text-xl" />
              </div>

              {/* Title */}
              {showTitles && image.title && (
                <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-gradient-to-t tw-from-black tw-to-transparent tw-px-3 tw-py-2">
                  <p className="tw-text-white tw-text-sm tw-truncate">
                    {image.title}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Detail View */}
      {isOpen && currentImage && (
        <div
          className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-90 tw-z-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-4"
          onClick={handleClose}
          role="dialog"
          aria-modal="true"
          aria-label="Image detail view"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="tw-absolute tw-top-4 tw-right-4 tw-text-white tw-p-2 hover:tw-bg-white hover:tw-bg-opacity-20 tw-rounded tw-transition-colors"
            aria-label="Close gallery"
          >
            <FaTimes className="tw-text-2xl" />
          </button>

          {/* Header with Title and Counter */}
          <div className="tw-absolute tw-top-4 tw-left-4 tw-text-white">
            {currentImage.title && (
              <h3 className="tw-text-lg tw-font-semibold tw-mb-1">
                {currentImage.title}
              </h3>
            )}
            <p className="tw-text-sm tw-opacity-75">
              {selectedIndex + 1} / {images.length}
            </p>
          </div>

          {/* Main Image Container */}
          <div className="tw-flex tw-items-center tw-justify-center tw-flex-1 tw-w-full">
            <img
              src={currentImage.url}
              alt={currentImage.title || 'Gallery detail view'}
              className="tw-max-h-[80vh] tw-max-w-[90vw] tw-object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Bottom Controls */}
          <div className="tw-absolute tw-bottom-4 tw-left-0 tw-right-0 tw-flex tw-items-center tw-justify-center tw-gap-4">
            {/* Previous Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
              className="tw-bg-white tw-bg-opacity-20 hover:tw-bg-opacity-40 tw-text-white tw-p-3 tw-rounded-xl tw-transition-colors"
              aria-label="Previous image"
            >
              <FaChevronLeft className="tw-text-xl" />
            </button>

            {/* Download Button */}
            {showDownload && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                className="tw-bg-white tw-bg-opacity-20 hover:tw-bg-opacity-40 tw-text-white tw-p-3 tw-rounded-xl tw-transition-colors"
                aria-label="Download image"
              >
                <FaDownload className="tw-text-xl" />
              </button>
            )}

            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              className="tw-bg-white tw-bg-opacity-20 hover:tw-bg-opacity-40 tw-text-white tw-p-3 tw-rounded-xl tw-transition-colors"
              aria-label="Next image"
            >
              <FaChevronRight className="tw-text-xl" />
            </button>
          </div>

          {/* Keyboard Hint */}
          <div className="tw-absolute tw-bottom-4 tw-left-4 tw-text-white tw-text-xs tw-opacity-50">
            <p>← → Arrow keys to navigate • ESC to close</p>
          </div>
        </div>
      )}
    </>
  );
};

ImageGallery.propTypes = {
  /** Array of image objects with id, url, and optional title */
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
    })
  ),
  /** Number of columns for thumbnail grid (1-6) */
  columns: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  /** Gap between thumbnails */
  gap: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Show download button in detail view */
  showDownload: PropTypes.bool,
  /** Show image titles under thumbnails */
  showTitles: PropTypes.bool,
  /** Tailwind height class for thumbnails */
  thumbnailHeight: PropTypes.string,
  /** Callback when image is clicked */
  onImageClick: PropTypes.func,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default ImageGallery;
